/**
 * 世界时钟主要功能
 */

export class WorldClock {
  constructor() {
    this.timezones = this.loadTimezones();
    this.updateInterval = null;
    this.selectedTimezone = null;
    this.activeColumnHour = null;
    this.activeColumnCell = null;
    this.activeColumnBounds = null;
    this.columnBoundaryMap = [];
    
    this.init();
  }

  /**
   * 初始化
   */
  init() {
    this.initElements();
    this.bindEvents();
    this.updateColumnBoundaryMap();
    this.renderTimezones();
    this.startTimeUpdate();
  }

  /**
   * 初始化DOM元素
   */
  initElements() {
    this.elements = {
      timezoneRows: document.getElementById('timezone-rows'),
      searchInput: document.getElementById('timezone-search'),
      clearSearch: document.getElementById('clear-search'),
      dropdown: document.getElementById('timezone-dropdown'),
      options: document.getElementById('timezone-options'),
      noResults: document.getElementById('no-results'),
      // GMT帮助相关元素
      gmtHelpBtn: document.getElementById('gmt-help-trigger'),
      gmtHelpTooltip: document.getElementById('gmt-help-tooltip'),
      worldClockBody: document.querySelector('.world-clock-body'),
      columnHighlight: document.querySelector('.column-hover-outline')
    };
  }

  /**
   * 绑定事件
   */
  bindEvents() {
    // 搜索输入
    this.elements.searchInput.addEventListener('input', this.handleSearch.bind(this));
    this.elements.searchInput.addEventListener('focus', this.handleSearchFocus.bind(this));
    this.elements.searchInput.addEventListener('blur', this.handleSearchBlur.bind(this));
    
    // 清除搜索
    this.elements.clearSearch.addEventListener('click', this.clearSearch.bind(this));
    
    // 点击外部关闭下拉菜单
    document.addEventListener('click', this.handleDocumentClick.bind(this));
    
    // 键盘导航
    this.elements.searchInput.addEventListener('keydown', this.handleKeydown.bind(this));
    
    // GMT帮助按钮
    if (this.elements.gmtHelpBtn && this.elements.gmtHelpTooltip) {
      this.bindGmtHelpEvents();
    }

    this.bindColumnHoverEvents();
  }

  bindColumnHoverEvents() {
    const { timezoneRows, worldClockBody, columnHighlight } = this.elements;
    if (!timezoneRows || !worldClockBody || !columnHighlight) {
      return;
    }

    timezoneRows.addEventListener('pointerover', (event) => {
      const cell = event.target.closest('.time-cell');
      if (!cell || !timezoneRows.contains(cell)) {
        return;
      }
      this.showColumnHighlight(cell);
    });

    timezoneRows.addEventListener('pointermove', (event) => {
      const cell = event.target.closest('.time-cell');
      if (cell && timezoneRows.contains(cell)) {
        this.showColumnHighlight(cell);
        return;
      }

      const inferredHour = this.inferHourFromPointer(event);
      if (inferredHour !== null) {
        this.showColumnHighlightByHour(inferredHour);
        return;
      }

      if (this.activeColumnHour !== null && this.isPointerWithinActiveColumn(event)) {
        this.updateColumnHighlightPosition();
        return;
      }

      this.hideColumnHighlight();
    });

    worldClockBody.addEventListener('pointerleave', () => {
      this.hideColumnHighlight();
    });

    worldClockBody.addEventListener('scroll', () => {
      this.updateColumnHighlightPosition();
    });

    timezoneRows.addEventListener('scroll', () => {
      this.updateColumnHighlightPosition();
    }, true);

    window.addEventListener('resize', () => {
      this.updateColumnBoundaryMap();
      this.updateColumnHighlightPosition();
    });
  }

  showColumnHighlight(cell) {
    const { columnHighlight } = this.elements;
    if (!columnHighlight) {
      return;
    }

    const hour = cell.dataset.hour;
    if (typeof hour === 'undefined') {
      return;
    }

    this.activeColumnHour = hour;
    this.activeColumnCell = cell;
    this.updateColumnHighlightPosition();
  }

  showColumnHighlightByHour(hour) {
    const { timezoneRows } = this.elements;
    if (!timezoneRows) {
      return;
    }

    const referenceCell = timezoneRows.querySelector(`.time-cell[data-hour="${hour}"]`);
    if (!referenceCell) {
      return;
    }

    this.activeColumnHour = hour;
    this.activeColumnCell = referenceCell;
    this.updateColumnHighlightPosition();
  }

  updateColumnHighlightPosition() {
    const { timezoneRows, worldClockBody, columnHighlight } = this.elements;
    if (!columnHighlight || !worldClockBody || !timezoneRows) {
      return;
    }

    if (this.activeColumnHour === null || typeof this.activeColumnHour === 'undefined') {
      return;
    }

    const cells = Array.from(timezoneRows.querySelectorAll(`.time-cell[data-hour="${this.activeColumnHour}"]`));
    if (!cells.length) {
      this.hideColumnHighlight();
      return;
    }

    if (!this.activeColumnCell || !this.activeColumnCell.isConnected) {
      this.activeColumnCell = cells[0];
    }

    const bodyRect = worldClockBody.getBoundingClientRect();
    let minTop = Infinity;
    let maxBottom = -Infinity;
    let minLeft = Infinity;
    let maxRight = -Infinity;

    cells.forEach((item) => {
      const rect = item.getBoundingClientRect();
      if (rect.top < minTop) {
        minTop = rect.top;
      }
      if (rect.bottom > maxBottom) {
        maxBottom = rect.bottom;
      }
      if (rect.left < minLeft) {
        minLeft = rect.left;
      }
      if (rect.right > maxRight) {
        maxRight = rect.right;
      }
    });

    const top = Math.max(0, minTop - bodyRect.top);
    const height = maxBottom - minTop;
    const left = Math.max(0, minLeft - bodyRect.left);
    const width = Math.max(1, maxRight - minLeft);

    columnHighlight.style.top = `${top}px`;
    columnHighlight.style.height = `${height}px`;
    columnHighlight.style.left = `${left}px`;
    columnHighlight.style.width = `${width}px`;

    columnHighlight.classList.remove('hidden');
    columnHighlight.classList.add('is-active');

    this.activeColumnBounds = {
      left: minLeft,
      right: maxRight
    };
  }

  isPointerWithinActiveColumn(event) {
    const { columnHighlight } = this.elements;
    if (!columnHighlight || columnHighlight.classList.contains('hidden') || !this.activeColumnBounds) {
      return false;
    }

    const { clientX, clientY } = event;
    const { left, right } = this.activeColumnBounds;
    const buffer = 16;

    if (clientX < left - buffer || clientX > right + buffer) {
      return false;
    }

    const rect = columnHighlight.getBoundingClientRect();
    return clientY >= rect.top && clientY <= rect.bottom;
  }

  hideColumnHighlight() {
    const { columnHighlight } = this.elements;
    if (!columnHighlight) {
      return;
    }

    this.activeColumnHour = null;
    this.activeColumnCell = null;
    this.activeColumnBounds = null;
    columnHighlight.classList.remove('is-active');
    columnHighlight.classList.add('hidden');
  }

  updateColumnBoundaryMap() {
    const gmtRow = document.querySelector('.gmt-row .grid-cols-layout');
    if (!gmtRow) {
      this.columnBoundaryMap = [];
      return;
    }

    const headerCells = gmtRow.querySelectorAll('.time-cell[data-hour]');
    this.columnBoundaryMap = Array.from(headerCells).map((cell) => {
      const hour = cell.dataset.hour;
      const rect = cell.getBoundingClientRect();
      return {
        hour,
        left: rect.left,
        right: rect.right
      };
    });
  }

  inferHourFromPointer(event) {
    this.updateColumnBoundaryMap();
    if (!this.columnBoundaryMap.length) {
      return null;
    }

    const { clientX } = event;
    for (let i = 0; i < this.columnBoundaryMap.length; i += 1) {
      const boundary = this.columnBoundaryMap[i];
      if (clientX >= boundary.left && clientX <= boundary.right) {
        return boundary.hour;
      }
    }

    return null;
  }

  /**
   * 处理搜索输入
   */
  handleSearch(e) {
    const query = e.target.value.trim();
    
    // 显示/隐藏清除按钮
    if (query.length > 0) {
      this.elements.clearSearch.classList.remove('hidden');
    } else {
      this.elements.clearSearch.classList.add('hidden');
    }
    
    // 重置选择状态
    this.selectedTimezone = null;
    
    // 修改为1个字符就开始搜索
    if (query.length < 1) {
      this.hideDropdown();
      return;
    }
    
    this.showDropdown();
    this.searchTimezones(query);
  }

  /**
   * 搜索时区
   */
  searchTimezones(query) {
    const results = window.searchTimezones(query);
    this.renderSearchResults(results);
  }

  /**
   * 渲染搜索结果
   */
  renderSearchResults(results) {
    // 首先清空选项列表和隐藏无结果消息
    this.elements.options.innerHTML = '';
    this.hideNoResults();
    
    if (results.length === 0) {
      this.showNoResults();
      return;
    }
    
    const template = document.getElementById('timezone-option-template');
    
    results.forEach(tz => {
      const option = template.content.cloneNode(true);
      
      const optionDiv = option.querySelector('.timezone-option');
      optionDiv.querySelector('.timezone-city').textContent = `${tz.city} (${tz.country})`;
      
      // 隐藏时区代码和偏移量，避免静态vs动态数据不一致问题
      optionDiv.querySelector('.timezone-code').style.display = 'none';
      optionDiv.querySelector('.timezone-offset').style.display = 'none';
      
      // 显示当前时间
      const currentTime = this.getCurrentTimeForTimezone(tz.timezone);
      optionDiv.querySelector('.timezone-time').textContent = currentTime.time;
      
      optionDiv.addEventListener('click', () => {
        this.addTimezoneDirectly(tz);
      });
      
      this.elements.options.appendChild(option);
    });
  }

  /**
   * 直接添加时区（新的交互方式）
   */
  addTimezoneDirectly(tz) {
    this.timezones.push(tz);
    this.saveTimezones();
    this.renderTimezones();
    this.clearSearch();
  }

  /**
   * 选择时区（保留但不再主要使用）
   */
  selectTimezone(tz) {
    this.selectedTimezone = tz;
    this.elements.searchInput.value = `${tz.city} (${tz.country})`;
    this.hideDropdown();
  }

  /**
   * 清除搜索
   */
  clearSearch() {
    this.elements.searchInput.value = '';
    this.elements.clearSearch.classList.add('hidden');
    this.selectedTimezone = null;
    this.hideDropdown();
  }

  /**
   * 渲染时区列表
   */
  renderTimezones() {
    if (!this.elements.timezoneRows) {
      return;
    }

    this.hideColumnHighlight();
    this.updateColumnBoundaryMap();

    // 保留GMT行，清除其他行
    const gmtRow = this.elements.timezoneRows.querySelector('.gmt-row');

    // 只删除非GMT行（时区卡片）
    const existingTimezoneRows = this.elements.timezoneRows.querySelectorAll('.timezone-row-wrapper');
    existingTimezoneRows.forEach(row => row.remove());
    
    // 渲染添加的时区
    this.timezones.forEach((tz, index) => {
      const row = this.createTimezoneRow(tz, index);
      this.elements.timezoneRows.appendChild(row);
    });

    // 更新所有时间
    this.updateAllTimes();
  }

  /**
   * 创建时区行
   */
  createTimezoneRow(tz, index) {
    const template = document.getElementById('timezone-row-template');
    const row = template.content.cloneNode(true);
    
    // 获取新的DOM结构元素
    const wrapperDiv = row.querySelector('.timezone-row-wrapper');
    const rowDiv = row.querySelector('.timezone-row');
    const deleteBtn = row.querySelector('.delete-btn');
    
    // 设置时区数据属性
    rowDiv.dataset.timezone = tz.timezone;
    rowDiv.dataset.index = index;
    
    // 设置时区信息，使用增强的动态时区数据（无太阳图标）
    const enhancedTz = window.getEnhancedTimezoneData(tz);
    const displayCode = enhancedTz.displayCode || enhancedTz.code;
    const displayOffset = enhancedTz.displayOffset || enhancedTz.offset;
    
    rowDiv.querySelector('.timezone-name').textContent = tz.city;
    rowDiv.querySelector('.timezone-code').textContent = `${displayCode} = GMT${this.formatOffset(displayOffset)}`;
    rowDiv.querySelector('.country-name').textContent = tz.country;
    
    // 获取grid布局容器
    const gridLayout = rowDiv.querySelector('.grid-cols-layout');
    
    // 在当前时间列后面插入24个时间格子
    const currentTimeDiv = gridLayout.children[1]; // 第二个div是当前时间列
    
    // 生成24个时间格子，插入到当前时间列之后
    for (let hour = 0; hour < 24; hour++) {
      const cell = document.createElement('div');
      cell.className = 'time-cell';
      cell.dataset.hour = hour;
      
      const timeValue = document.createElement('div');
      timeValue.className = 'time-value';
      timeValue.textContent = '--';
      
      const minuteValue = document.createElement('div');
      minuteValue.className = 'minute-value hidden';
      minuteValue.textContent = ':00';
      
      const dayIndicator = document.createElement('div');
      dayIndicator.className = 'day-indicator hidden';
      
      cell.appendChild(timeValue);
      cell.appendChild(minuteValue);
      cell.appendChild(dayIndicator);
      
      // 直接添加到grid layout末尾
      gridLayout.appendChild(cell);
    }
    
    // 删除按钮事件（绝对定位在wrapper中）
    deleteBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.removeTimezone(index);
    });
    
    // 拖拽事件（绑定到内容区域）
    this.bindDragEvents(rowDiv);
    
    return row;
  }

  /**
   * 绑定拖拽事件
   */
  bindDragEvents(rowDiv) {
    rowDiv.addEventListener('dragstart', this.handleDragStart.bind(this));
    rowDiv.addEventListener('dragover', this.handleDragOver.bind(this));
    rowDiv.addEventListener('drop', this.handleDrop.bind(this));
    rowDiv.addEventListener('dragend', this.handleDragEnd.bind(this));
  }

  /**
   * 移除时区
   */
  removeTimezone(index) {
    this.timezones.splice(index, 1);
    this.saveTimezones();
    this.renderTimezones();
  }

  /**
   * 获取指定时区的当前时间（分为时间和日期两部分）
   */
  getCurrentTimeForTimezone(timezone) {
    try {
      const now = new Date();
      
      // 获取时间部分 (HH:MM)
      const timeFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      
      // 获取日期部分 (Day MonDD)
      const dateFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        weekday: 'short',
        month: 'short',
        day: '2-digit'
      });
      
      const timeFormatted = timeFormatter.format(now);
      const dateFormatted = dateFormatter.format(now);
      
      return { time: timeFormatted, date: dateFormatted };
    } catch (error) {
      return { time: '--:--', date: '--- ---' };
    }
  }

  /**
   * 计算指定时区在GMT某小时的本地时间
   */
  calculateTimeForGMTHour(timezone, gmtHour) {
    try {
      // 创建今天GMT指定小时的时间
      const today = new Date();
      const gmtDate = new Date(Date.UTC(
        today.getUTCFullYear(),
        today.getUTCMonth(),
        today.getUTCDate(),
        gmtHour,
        0,
        0,
        0
      ));
      
      // 转换到目标时区
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour12: false
      });
      
      const parts = formatter.formatToParts(gmtDate);
      const hour = parts.find(part => part.type === 'hour')?.value || '00';
      const minute = parts.find(part => part.type === 'minute')?.value || '00';
      const day = parseInt(parts.find(part => part.type === 'day')?.value || '1');
      const month = parseInt(parts.find(part => part.type === 'month')?.value || '1');
      const year = parseInt(parts.find(part => part.type === 'year')?.value || '2024');
      
      // 计算是否跨天
      const gmtDay = gmtDate.getUTCDate();
      const gmtMonth = gmtDate.getUTCMonth() + 1;
      const gmtYear = gmtDate.getUTCFullYear();
      
      let dayDiff = 0;
      if (year > gmtYear || (year === gmtYear && month > gmtMonth) || (year === gmtYear && month === gmtMonth && day > gmtDay)) {
        dayDiff = 1;
      } else if (year < gmtYear || (year === gmtYear && month < gmtMonth) || (year === gmtYear && month === gmtMonth && day < gmtDay)) {
        dayDiff = -1;
      }
      
      return {
        hour,
        minute,
        dayDiff
      };
    } catch (error) {
      return {
        hour: '--',
        minute: '00',
        dayDiff: 0
      };
    }
  }

  /**
   * 更新所有时间
   */
  updateAllTimes() {
    // 更新GMT当前时间
    const gmtCurrentTime = document.getElementById('gmt-current-time');
    const gmtCurrentDate = document.getElementById('gmt-current-date');
    if (gmtCurrentTime && gmtCurrentDate) {
      const currentGmt = this.getCurrentTimeForTimezone('UTC');
      gmtCurrentTime.textContent = currentGmt.time;
      gmtCurrentDate.textContent = currentGmt.date;
    }
    
    // 更新所有时区行
    this.elements.timezoneRows.querySelectorAll('.timezone-row:not(.gmt-row)').forEach((rowDiv, index) => {
      const timezone = rowDiv.dataset.timezone;
      if (!timezone) return;
      
      // 获取对应的时区数据并增强（无太阳图标）
      const timezoneData = this.timezones[index];
      if (timezoneData) {
        const enhancedTz = window.getEnhancedTimezoneData(timezoneData);
        const displayCode = enhancedTz.displayCode || enhancedTz.code;
        const displayOffset = enhancedTz.displayOffset || enhancedTz.offset;
        
        // 更新时区代码和偏移量显示
        const timezoneCodeDiv = rowDiv.querySelector('.timezone-code');
        if (timezoneCodeDiv) {
          timezoneCodeDiv.textContent = `${displayCode} = GMT${this.formatOffset(displayOffset)}`;
        }
      }
      
      // 更新当前时间
      const currentTimeDiv = rowDiv.querySelector('.current-time');
      const currentDateDiv = rowDiv.querySelector('.current-date');
      if (currentTimeDiv && currentDateDiv) {
        const currentTime = this.getCurrentTimeForTimezone(timezone);
        currentTimeDiv.textContent = currentTime.time;
        currentDateDiv.textContent = currentTime.date;
      }
      
      // 更新24小时格子
      rowDiv.querySelectorAll('.time-cell').forEach(cell => {
        const gmtHour = parseInt(cell.dataset.hour);
        const timeInfo = this.calculateTimeForGMTHour(timezone, gmtHour);
        
        const timeValue = cell.querySelector('.time-value');
        const minuteValue = cell.querySelector('.minute-value');
        const dayIndicator = cell.querySelector('.day-indicator');
        
        if (timeValue) timeValue.textContent = timeInfo.hour;
        if (minuteValue) {
          if (timeInfo.minute !== '00') {
            minuteValue.textContent = `:${timeInfo.minute}`;
            minuteValue.classList.remove('hidden');
          } else {
            minuteValue.classList.add('hidden');
          }
        }
        
        if (dayIndicator) {
          if (timeInfo.dayDiff > 0) {
            dayIndicator.setAttribute('data-day', '1');
            dayIndicator.classList.remove('hidden');
          } else if (timeInfo.dayDiff < 0) {
            dayIndicator.setAttribute('data-day', '-1');
            dayIndicator.classList.remove('hidden');
          } else {
            dayIndicator.removeAttribute('data-day');
            dayIndicator.classList.add('hidden');
          }
        }
      });
    });
  }

  /**
   * 开始时间更新
   */
  startTimeUpdate() {
    // 每分钟更新一次
    this.updateInterval = setInterval(() => {
      this.updateAllTimes();
    }, 60000);
  }

  /**
   * 停止时间更新
   */
  stopTimeUpdate() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }

  /**
   * 保存时区到本地存储
   */
  saveTimezones() {
    try {
      localStorage.setItem('worldClockTimezones', JSON.stringify(this.timezones));
    } catch (error) {
      console.error('Failed to save timezones:', error);
    }
  }

  /**
   * 从本地存储加载时区
   */
  loadTimezones() {
    try {
      const saved = localStorage.getItem('worldClockTimezones');
      if (saved) {
        const timezones = JSON.parse(saved);
        // 清理重复的时区数据
        return this.removeDuplicateTimezones(timezones);
      }
      
      // 如果没有保存的时区，返回默认时区列表
      const defaultTimezones = [
        { city: 'New York', country: 'United States', timezone: 'America/New_York', code: 'EST', offset: '-5:00' },
        { city: 'Brasilia', country: 'Brazil', timezone: 'America/Sao_Paulo', code: 'BRT', offset: '-3:00' },
        { city: 'London', country: 'United Kingdom', timezone: 'Europe/London', code: 'GMT', offset: '+0:00' },
        { city: 'Sydney', country: 'Australia', timezone: 'Australia/Sydney', code: 'AEST', offset: '+10:00' }
      ];
      
      return defaultTimezones;
    } catch (error) {
      console.error('Failed to load timezones:', error);
      return [];
    }
  }

  /**
   * 清理重复的时区数据
   */
  removeDuplicateTimezones(timezones) {
    const unique = [];
    const seen = new Set();
    
    for (const tz of timezones) {
      const key = `${tz.city}-${tz.country}`;
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(tz);
      }
    }
    
    return unique;
  }

  /**
   * 显示下拉菜单
   */
  showDropdown() {
    this.elements.dropdown.classList.remove('hidden');
  }

  /**
   * 隐藏下拉菜单
   */
  hideDropdown() {
    this.elements.dropdown.classList.add('hidden');
  }


  /**
   * 显示无结果
   */
  showNoResults() {
    this.elements.noResults.classList.remove('hidden');
  }

  /**
   * 隐藏无结果
   */
  hideNoResults() {
    this.elements.noResults.classList.add('hidden');
  }

  /**
   * 处理搜索框焦点
   */
  handleSearchFocus() {
    if (this.elements.searchInput.value.trim().length >= 2) {
      this.showDropdown();
    }
  }

  /**
   * 处理搜索框失去焦点
   */
  handleSearchBlur() {
    // 延迟隐藏，允许点击选项
    setTimeout(() => {
      this.hideDropdown();
    }, 200);
  }

  /**
   * 处理文档点击
   */
  handleDocumentClick(e) {
    if (!this.elements.searchInput.contains(e.target) && 
        !this.elements.dropdown.contains(e.target)) {
      this.hideDropdown();
    }
  }

  /**
   * 处理键盘导航
   */
  handleKeydown(e) {
    if (e.key === 'Escape') {
      this.hideDropdown();
      this.elements.searchInput.blur();
    }
  }

  /**
   * 拖拽开始
   */
  handleDragStart(e) {
    this.draggedElement = e.target.closest('.timezone-row');
    this.draggedIndex = parseInt(this.draggedElement.dataset.index);
    e.dataTransfer.effectAllowed = 'move';
    
    // 添加拖拽样式
    this.draggedElement.classList.add('opacity-50');
  }

  /**
   * 拖拽悬停
   */
  handleDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    const targetRow = e.target.closest('.timezone-row');
    if (targetRow && targetRow !== this.draggedElement && !targetRow.classList.contains('gmt-row')) {
      // 添加拖拽指示器
      targetRow.classList.add('border-blue-500', 'border-2');
    }
  }

  /**
   * 拖拽放置
   */
  handleDrop(e) {
    e.preventDefault();
    
    const targetRow = e.target.closest('.timezone-row');
    if (!targetRow || targetRow === this.draggedElement || targetRow.classList.contains('gmt-row')) {
      return;
    }
    
    const targetIndex = parseInt(targetRow.dataset.index);
    
    // 重新排序时区数组
    const draggedTimezone = this.timezones[this.draggedIndex];
    this.timezones.splice(this.draggedIndex, 1);
    this.timezones.splice(targetIndex, 0, draggedTimezone);
    
    this.saveTimezones();
    this.renderTimezones();
  }

  /**
   * 拖拽结束
   */
  handleDragEnd(e) {
    // 清除拖拽样式
    document.querySelectorAll('.timezone-row').forEach(row => {
      row.classList.remove('opacity-50', 'border-blue-500', 'border-2');
    });
    
    this.draggedElement = null;
    this.draggedIndex = null;
  }

  /**
   * 绑定GMT帮助按钮事件
   */
  bindGmtHelpEvents() {
    const btn = this.elements.gmtHelpBtn;
    const tooltip = this.elements.gmtHelpTooltip;
    
    // 检测设备是否支持hover
    const hasHover = window.matchMedia('(hover: hover)').matches;
    
    if (hasHover) {
      // PC端：仅悬浮显示，禁用点击
      btn.addEventListener('mouseenter', () => {
        this.showGmtHelp();
      });
      
      btn.addEventListener('mouseleave', () => {
        // 延迟隐藏，给用户时间移动到弹窗上
        setTimeout(() => {
          if (!tooltip.matches(':hover') && !btn.matches(':hover')) {
            this.hideGmtHelp();
          }
        }, 100);
      });
      
      // 弹窗本身的悬浮事件
      tooltip.addEventListener('mouseenter', () => {
        this.showGmtHelp();
      });
      
      tooltip.addEventListener('mouseleave', () => {
        this.hideGmtHelp();
      });
      
      // 禁用PC端的点击事件
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      });
    } else {
      // 移动端：点击显示/隐藏
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleGmtHelp();
      });
      
      // 点击外部关闭
      document.addEventListener('click', (e) => {
        if (!tooltip.contains(e.target) && !btn.contains(e.target)) {
          this.hideGmtHelp();
        }
      });
    }
    
    // ESC键关闭
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.hideGmtHelp();
      }
    });
    
    // 滚动时更新弹窗位置
    this.bindScrollEvent();
  }
  
  /**
   * 绑定滚动事件
   */
  bindScrollEvent() {
    let ticking = false;
    
    const handleScroll = () => {
      // 如果弹窗不可见，不处理
      const tooltip = this.elements.gmtHelpTooltip;
      if (!tooltip || tooltip.classList.contains('hidden')) {
        return;
      }
      
      // 使用 requestAnimationFrame 替代 setTimeout，更流畅
      if (!ticking) {
        requestAnimationFrame(() => {
          const btn = this.elements.gmtHelpBtn;
          if (btn && tooltip) {
            this.positionTooltip(btn, tooltip);
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    
    // 监听滚动事件
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('scroll', handleScroll, { passive: true });
  }
  
  /**
   * 显示GMT帮助弹窗
   */
  showGmtHelp() {
    const btn = this.elements.gmtHelpBtn;
    const tooltip = this.elements.gmtHelpTooltip;
    
    if (!btn || !tooltip) return;
    
    // 计算位置
    this.positionTooltip(btn, tooltip);
    
    // 显示弹窗
    tooltip.classList.remove('hidden');
    btn.classList.add('active');
  }
  
  /**
   * 隐藏GMT帮助弹窗
   */
  hideGmtHelp() {
    const tooltip = this.elements.gmtHelpTooltip;
    const btn = this.elements.gmtHelpBtn;
    
    if (tooltip) {
      tooltip.classList.add('hidden');
    }
    if (btn) {
      btn.classList.remove('active');
    }
  }
  
  /**
   * 切换GMT帮助弹窗
   */
  toggleGmtHelp() {
    const tooltip = this.elements.gmtHelpTooltip;
    if (tooltip && tooltip.classList.contains('hidden')) {
      this.showGmtHelp();
    } else {
      this.hideGmtHelp();
    }
  }
  
  /**
   * 计算弹窗位置
   */
  positionTooltip(trigger, tooltip) {
    const triggerRect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // 根据屏幕大小动态计算弹窗宽度
    let tooltipWidth = 360;
    if (viewportWidth <= 480) {
      tooltipWidth = viewportWidth - 20; // 小屏幕时自适应宽度
    }
    
    // 默认位置：触发器下方，稍微向右偏移
    let left = triggerRect.left + (triggerRect.width / 2) - (tooltipWidth / 2);
    let top = triggerRect.bottom + 8;
    
    // 根据屏幕大小调整最小左边距
    const minLeftMargin = viewportWidth > 768 ? 100 : 10; // PC端100px，移动端10px
    
    // 水平边界检查
    if (left < minLeftMargin) {
      left = minLeftMargin;
    } else if (left + tooltipWidth > viewportWidth - 10) {
      left = viewportWidth - tooltipWidth - 10;
    }
    
    // 小屏幕时强制居中
    if (viewportWidth <= 480) {
      left = 10; // 距离左边缘10px
    }
    
    // 垂直边界检查 - 暂时获取大概高度
    const estimatedHeight = 200; // 估算高度
    if (top + estimatedHeight > viewportHeight - 8) {
      // 如果底部空间不足，放在触发器上方
      top = triggerRect.top - estimatedHeight - 8;
      
      // 调整箭头位置（指向下方）
      const arrow = tooltip.querySelector('.tooltip-arrow');
      if (arrow) {
        arrow.style.borderWidth = '8px 8px 0 8px';
        arrow.style.borderColor = 'var(--wc-border) transparent transparent transparent';
        arrow.style.top = 'auto';
        arrow.style.bottom = '-8px';
      }
    } else {
      // 重置箭头到默认位置（指向上方）
      const arrow = tooltip.querySelector('.tooltip-arrow');
      if (arrow) {
        arrow.style.borderWidth = '0 8px 8px 8px';
        arrow.style.borderColor = 'transparent transparent var(--wc-border) transparent';
        arrow.style.top = '-8px';
        arrow.style.bottom = 'auto';
      }
    }
    
    // 计算箭头水平位置（相对于弹窗左边缘）
    const triggerCenterX = triggerRect.left + (triggerRect.width / 2);
    const arrowLeft = Math.max(16, Math.min(triggerCenterX - left, tooltipWidth - 16));
    const arrow = tooltip.querySelector('.tooltip-arrow');
    if (arrow) {
      arrow.style.left = `${arrowLeft}px`;
    }
    
    // 使用 transform 替代 left/top，性能更好
    tooltip.style.transform = `translate(${left}px, ${top}px)`;
    tooltip.style.left = '0';
    tooltip.style.top = '0';
  }


  /**
   * 格式化时区偏移显示
   * 整点时间只显示小时，非整点显示完整偏移
   */
  formatOffset(offset) {
    // 如果是整点（如+5:00, +8:00, -5:00），只显示小时部分
    if (offset.endsWith(':00')) {
      return offset.slice(0, -3); // +5:00 → +5, -5:00 → -5
    }
    // 特殊时间（如+5:45, +9:30），显示完整偏移
    return offset; // +5:45 保持不变
  }

  /**
   * 销毁实例
   */
  destroy() {
    this.stopTimeUpdate();
    
    // 移除事件监听器
    if (this.elements.searchInput) {
      this.elements.searchInput.removeEventListener('input', this.handleSearch);
      this.elements.searchInput.removeEventListener('focus', this.handleSearchFocus);
      this.elements.searchInput.removeEventListener('blur', this.handleSearchBlur);
      this.elements.searchInput.removeEventListener('keydown', this.handleKeydown);
    }
    
    document.removeEventListener('click', this.handleDocumentClick);
  }
}

window.WorldClock = WorldClock;

let worldClockInstance = null;

export function initWorldClock() {
  const root = document.querySelector('.world-clock-main');
  if (!root) {
    return null;
  }

  if (!worldClockInstance) {
    try {
      worldClockInstance = new WorldClock();
      window.worldClock = worldClockInstance;
    } catch (error) {
      console.error('Failed to create WorldClock instance:', error);
      worldClockInstance = null;
    }
  }
  return worldClockInstance;
}
